import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { api } from "@/services/api.config";
import { useToast } from "vue-toastification";
import { keywordsLogger, errorsLogger } from "@/utils/logger";
import {
  useGlobalUndoRedo,
  type ActionData,
} from "@/composables/useUndoRedoWithStorage";
import i18n from "@/i18n";
import { languageDetector } from "@/utils/languageDetector";
import { isProductKeyword } from "@/utils/productKeywordFilter";
import { sortClusterTree } from "@/utils/clusterOrder";

const toast = useToast();
const { addAction } = useGlobalUndoRedo();
const t = i18n.global.t;

export interface Keyword {
  id: number;
  project_id: number;
  keyword: string;
  base_keyword: string;
  volume: number | null;
  competition: string | null;
  intent: string | null;
  source: "manual" | "google" | "serpstat";
  status: "active" | "deleted" | "excluded";
  cleaning_status: "pending" | "cleaned" | "excluded" | null;
  filters_applied: any | null;
  excluded_by_filter: string | null;
  cluster_id: number | null;
  cluster?: Cluster;
  metadata: any | null;
  created_at: string;
  updated_at: string;
}

export interface Cluster {
  id: number;
  name: string;
  project_id: number;
  parent_id?: number | null;
  mode: string;
  keywords?: Array<{
    id?: number | string;
    text: string;
    volume: number;
  }>;
  keywords_count?: number;
  total_volume?: number;
  children?: Cluster[];
  is_unclustered?: boolean;
  created_at: string;
  updated_at: string;
}

export interface KeywordStats {
  total: number;
  cleaned: number;
  excluded: number;
  clustered: number;
}

// Интерфейс для расширенных фильтров
export interface AdvancedFilters {
  minVolume: number | null;
  maxVolume: number | null;
  minLength: number | null;
  maxLength: number | null;
  containsWords: string;
  excludeWords: string;
  regex: string;
  excludeNumbers: boolean;
  excludeSpecialChars: boolean;
  excludeDuplicates: boolean;
  productSearchMode?: "none" | "alphanumeric" | "smart";
  intents?: {
    informational: boolean;
    navigational: boolean;
    commercial: boolean;
    transactional: boolean;
  };
}

// Новые типы для групповых фильтров
export interface FilterCondition {
  id: string;
  field: "phrase" | "volume";
  operator: string; // Расширенный список операторов
  value: string | number; // Can be string for phrase filters or number for volume filters
}

export interface FilterGroup {
  id: string;
  type: "group";
  operator: "AND" | "OR";
  children: (FilterCondition | FilterGroup)[];
}

export const useKeywordsStore = defineStore("keywords", () => {
  // State
  const projectId = ref<string | null>(null);
  const project = ref<any>(null); // Добавляем информацию о проекте
  const keywords = ref<Keyword[]>([]);
  const stats = ref<KeywordStats>({
    total: 0,
    cleaned: 0,
    excluded: 0,
    clustered: 0,
  });
  const clusters = ref<Cluster[]>([]);
  const activeClusterFilter = ref<number | string | (number | string)[] | null>(
    null,
  );
  const selectedKeywordIds = ref<Set<number>>(new Set());
  const isLoading = ref(false);
  const isProcessingClusters = ref(false); // Для отслеживания операций с кластерами
  const processingOperation = ref<
    "move-keywords" | "move-cluster" | "rename" | "delete" | null
  >(null); // Тип текущей операции
  const currentTab = ref<"keywords" | "cleaning" | "clusters">("keywords");

  // Фильтры на клиенте
  const filters = ref({
    search: "",
    minVolume: null as number | null,
    maxVolume: null as number | null,
    minLength: null as number | null,
    containsWords: "",
    excludeWords: "",
    sortBy: "volume" as "keyword" | "volume" | "created_at",
    sortOrder: "desc" as "asc" | "desc",
    showExcluded: false,
  });

  // Расширенные фильтры
  const activeFilters = ref<AdvancedFilters | null>(null);
  const activeGroupFilters = ref<FilterGroup | null>(null);
  const quickFilterText = ref("");

  const showExcludedMode = ref(false);

  // Computed - фильтрация ключей в зависимости от вкладки
  const filteredByTab = computed(() => {
    let result = keywords.value;

    // Если включен режим просмотра удаленных
    if (showExcludedMode.value) {
      result = result.filter((k) => k.status === "excluded");
    } else {
      // На всех вкладках показываем все активные ключи
      // Вкладки отличаются только доступным функционалом, а не списком ключей
      result = result.filter((k) => k.status === "active");
    }

    // Фильтрация по активному кластеру
    if (activeClusterFilter.value !== null) {
      keywordsLogger.log("Filtering by cluster:", activeClusterFilter.value);
      keywordsLogger.log("Keywords before cluster filter:", result.length);

      if (activeClusterFilter.value === "unclustered") {
        result = result.filter((k) => !k.cluster_id);
        keywordsLogger.log("Unclustered keywords:", result.length);
      } else if (Array.isArray(activeClusterFilter.value)) {
        // Множественный выбор кластеров
        const allClusterIds: number[] = [];
        const hasUnclustered =
          activeClusterFilter.value.includes("unclustered");

        activeClusterFilter.value.forEach((clusterId) => {
          if (typeof clusterId === "number") {
            // Получаем все ID дочерних кластеров для каждого выбранного
            const ids = getClusterWithChildrenIds(clusterId);
            keywordsLogger.log(`Cluster ${clusterId} with children:`, ids);
            allClusterIds.push(...ids);
          }
        });

        keywordsLogger.log(
          "Multiple clusters selected:",
          activeClusterFilter.value,
        );
        keywordsLogger.log(
          "All cluster IDs (including children):",
          allClusterIds,
        );
        keywordsLogger.log("Has unclustered:", hasUnclustered);

        // Фильтруем по всем выбранным кластерам
        result = result.filter((k) => {
          // Если выбраны некластеризованные и у ключа нет cluster_id
          if (hasUnclustered && !k.cluster_id) return true;
          // Если у ключа есть cluster_id и он в списке выбранных
          if (k.cluster_id && allClusterIds.includes(k.cluster_id)) return true;
          return false;
        });

        keywordsLogger.log(
          "Keywords after multiple cluster filter:",
          result.length,
        );
        if (result.length > 0) {
          keywordsLogger.log(
            "Sample filtered keywords:",
            result.slice(0, 3).map((k) => ({
              id: k.id,
              keyword: k.keyword,
              cluster_id: k.cluster_id,
            })),
          );
        }
      } else if (typeof activeClusterFilter.value === "number") {
        // Получаем все ID дочерних кластеров
        const clusterIds = getClusterWithChildrenIds(activeClusterFilter.value);
        keywordsLogger.log("Cluster IDs to filter:", clusterIds);
        keywordsLogger.log(
          "Sample keywords with cluster_id:",
          result
            .filter((k) => k.cluster_id)
            .slice(0, 3)
            .map((k) => ({
              id: k.id,
              keyword: k.keyword,
              cluster_id: k.cluster_id,
            })),
        );

        // Фильтруем по кластеру
        result = result.filter(
          (k) => k.cluster_id && clusterIds.includes(k.cluster_id),
        );
        keywordsLogger.log("Keywords after cluster filter:", result.length);
        keywordsLogger.log(
          "Filtered keywords sample:",
          result.slice(0, 3).map((k) => ({
            id: k.id,
            keyword: k.keyword,
            cluster_id: k.cluster_id,
          })),
        );
      }
    }

    // Показывать исключенные только если включен фильтр (устаревшее, оставлено для совместимости)
    if (filters.value.showExcluded && !showExcludedMode.value) {
      result = keywords.value; // Показываем все, включая excluded
    }

    return result;
  });

  // Computed - полная фильтрация с учетом пользовательских фильтров
  const filteredKeywords = computed(() => {
    let result = [...filteredByTab.value];

    // Поиск
    if (filters.value.search) {
      const searchLower = filters.value.search.toLowerCase();
      result = result.filter(
        (kw) =>
          kw.keyword.toLowerCase().includes(searchLower) ||
          kw.base_keyword.toLowerCase().includes(searchLower),
      );
    }

    // Применяем быстрый фильтр
    if (quickFilterText.value) {
      const searchLower = quickFilterText.value.toLowerCase();
      result = result.filter((kw) =>
        kw.keyword.toLowerCase().includes(searchLower),
      );
    }

    // Применяем групповые фильтры если они активны
    if (activeGroupFilters.value) {
      result = result.filter((kw) =>
        evaluateFilterGroup(kw, activeGroupFilters.value!),
      );
    }

    // Применяем расширенные фильтры если они активны
    if (activeFilters.value) {
      const af = activeFilters.value;

      // Фильтр по объему
      if (
        af.minVolume !== null &&
        af.minVolume !== undefined &&
        !isNaN(Number(af.minVolume))
      ) {
        const minVal = Number(af.minVolume);
        result = result.filter((kw) => (kw.volume || 0) >= minVal);
      }
      if (
        af.maxVolume !== null &&
        af.maxVolume !== undefined &&
        !isNaN(Number(af.maxVolume))
      ) {
        const maxVal = Number(af.maxVolume);
        result = result.filter((kw) => (kw.volume || 0) <= maxVal);
      }

      // Фильтр по длине
      if (
        af.minLength !== null &&
        af.minLength !== undefined &&
        !isNaN(Number(af.minLength))
      ) {
        const minLen = Number(af.minLength);
        result = result.filter((kw) => kw.keyword.length >= minLen);
      }
      if (
        af.maxLength !== null &&
        af.maxLength !== undefined &&
        !isNaN(Number(af.maxLength))
      ) {
        const maxLen = Number(af.maxLength);
        result = result.filter((kw) => kw.keyword.length <= maxLen);
      }

      // Фильтр "содержит слова"
      if (af.containsWords) {
        const words = af.containsWords
          .split(",")
          .map((w) => w.trim().toLowerCase())
          .filter((w) => w);
        if (words.length > 0) {
          result = result.filter((kw) => {
            const kwLower = kw.keyword.toLowerCase();
            return words.some((word) => kwLower.includes(word));
          });
        }
      }

      // Фильтр "исключить слова"
      if (af.excludeWords) {
        const words = af.excludeWords
          .split(",")
          .map((w) => w.trim().toLowerCase())
          .filter((w) => w);
        if (words.length > 0) {
          result = result.filter((kw) => {
            const kwLower = kw.keyword.toLowerCase();
            return !words.some((word) => kwLower.includes(word));
          });
        }
      }

      // Регулярное выражение
      if (af.regex) {
        try {
          const regex = new RegExp(af.regex);
          result = result.filter((kw) => regex.test(kw.keyword));
        } catch (e) {
          errorsLogger.error("Invalid regex:", e);
        }
      }

      // Исключить слова с цифрами
      if (af.excludeNumbers) {
        result = result.filter((kw) => !/\d/.test(kw.keyword));
      }

      // Исключить слова со спецсимволами
      if (af.excludeSpecialChars) {
        result = result.filter((kw) => /^[\p{L}\s\d]+$/u.test(kw.keyword));
      }

      // Приховати дублікати
      if (af.excludeDuplicates) {
        const seen = new Set<string>();
        result = result.filter((kw) => {
          const normalized = kw.keyword.toLowerCase().trim();
          if (seen.has(normalized)) {
            return false;
          }
          seen.add(normalized);
          return true;
        });
      }

      // Фільтр по інтенту
      if (af.intents) {
        const selectedIntents = Object.entries(af.intents)
          .filter(([, v]) => v)
          .map(([k]) => k);
        // Фільтруємо тільки якщо не всі вибрані і не жоден
        if (selectedIntents.length > 0 && selectedIntents.length < 4) {
          result = result.filter((kw) => {
            if (!kw.intent) return false;
            const kwIntents = kw.intent.split(",");
            return kwIntents.some((i) => selectedIntents.includes(i));
          });
        }
      }

      // Пошук товарних ключів
      if (af.productSearchMode && af.productSearchMode !== "none") {
        if (af.productSearchMode === "alphanumeric") {
          // Регулярка для поиска слово-цифр, исключая единицы измерения
          const measurementUnits = [
            // Метрические
            "мм", // i18n-allow-cyrillic: дані: одиниці виміру/регулярки/назва кластера з БД
            "см", // i18n-allow-cyrillic: дані: одиниці виміру/регулярки/назва кластера з БД
            "дм", // i18n-allow-cyrillic: дані: одиниці виміру/регулярки/назва кластера з БД
            "м", // i18n-allow-cyrillic: дані: одиниці виміру/регулярки/назва кластера з БД
            "км", // i18n-allow-cyrillic: дані: одиниці виміру/регулярки/назва кластера з БД
            "мг", // i18n-allow-cyrillic: дані: одиниці виміру/регулярки/назва кластера з БД
            "г", // i18n-allow-cyrillic: дані: одиниці виміру/регулярки/назва кластера з БД
            "кг", // i18n-allow-cyrillic: дані: одиниці виміру/регулярки/назва кластера з БД
            "т", // i18n-allow-cyrillic: дані: одиниці виміру/регулярки/назва кластера з БД
            "мл", // i18n-allow-cyrillic: дані: одиниці виміру/регулярки/назва кластера з БД
            "л", // i18n-allow-cyrillic: дані: одиниці виміру/регулярки/назва кластера з БД
            "mm",
            "cm",
            "dm",
            "m",
            "km",
            "mg",
            "g",
            "kg",
            "t",
            "ml",
            "l",
            // Цифровые единицы
            "kb",
            "mb",
            "gb",
            "tb",
            "pb",
            "кб", // i18n-allow-cyrillic: дані: одиниці виміру/регулярки/назва кластера з БД
            "мб", // i18n-allow-cyrillic: дані: одиниці виміру/регулярки/назва кластера з БД
            "гб", // i18n-allow-cyrillic: дані: одиниці виміру/регулярки/назва кластера з БД
            "тб", // i18n-allow-cyrillic: дані: одиниці виміру/регулярки/назва кластера з БД
            "пб", // i18n-allow-cyrillic: дані: одиниці виміру/регулярки/назва кластера з БД
            // Время
            "мс", // i18n-allow-cyrillic: дані: одиниці виміру/регулярки/назва кластера з БД
            "с", // i18n-allow-cyrillic: дані: одиниці виміру/регулярки/назва кластера з БД
            "сек", // i18n-allow-cyrillic: дані: одиниці виміру/регулярки/назва кластера з БД
            "мин", // i18n-allow-cyrillic: дані: одиниці виміру/регулярки/назва кластера з БД
            "ч", // i18n-allow-cyrillic: дані: одиниці виміру/регулярки/назва кластера з БД
            "год", // i18n-allow-cyrillic: дані: одиниці виміру/регулярки/назва кластера з БД
            "ms",
            "s",
            "sec",
            "min",
            "h",
            "hr",
            "year",
            // Электричество
            "в", // i18n-allow-cyrillic: дані: одиниці виміру/регулярки/назва кластера з БД
            "вт", // i18n-allow-cyrillic: дані: одиниці виміру/регулярки/назва кластера з БД
            "квт", // i18n-allow-cyrillic: дані: одиниці виміру/регулярки/назва кластера з БД
            "а", // i18n-allow-cyrillic: дані: одиниці виміру/регулярки/назва кластера з БД
            "ма", // i18n-allow-cyrillic: дані: одиниці виміру/регулярки/назва кластера з БД
            "v",
            "w",
            "kw",
            "a",
            "ma",
            "mah",
            // Площадь и объем
            "м2", // i18n-allow-cyrillic: дані: одиниці виміру/регулярки/назва кластера з БД
            "м3", // i18n-allow-cyrillic: дані: одиниці виміру/регулярки/назва кластера з БД
            "км2", // i18n-allow-cyrillic: дані: одиниці виміру/регулярки/назва кластера з БД
            "км3", // i18n-allow-cyrillic: дані: одиниці виміру/регулярки/назва кластера з БД
            "m2",
            "m3",
            "km2",
            "km3",
            // Проценты и валюта
            "%",
            "грн", // i18n-allow-cyrillic: дані: одиниці виміру/регулярки/назва кластера з БД
            "uah",
            "usd",
            "$",
            "€",
            "eur",
          ].join("|");

          const alphanumericRegex = new RegExp(
            // Паттерн: буквы+цифры или цифры+буквы в одном слове, исключая единицы измерения
            `\\b(?!\\d+(?:${measurementUnits})\\b)(?:[a-zA-Zа-яА-ЯіІїЇєЄ]+[\\d]+[\\w]*|[\\d]+[a-zA-Zа-яА-ЯіІїЇєЄ]+[\\w]*)\\b`, // i18n-allow-cyrillic: дані: одиниці виміру/регулярки/назва кластера з БД
            "i",
          );

          result = result.filter((kw) => {
            const words = kw.keyword.split(/\s+/);
            return words.some((word) => alphanumericRegex.test(word));
          });
        } else if (af.productSearchMode === "smart") {
          // Интеллектуальное определение моделей
          result = result.filter((kw) => {
            const keyword = kw.keyword.toLowerCase();
            const volume = kw.volume || 0;

            // Паттерны моделей товаров
            const modelPatterns = [
              // Электроника: RTX 4090, GTX 1660, RX 6800
              /\b(rtx|gtx|rx|ryzen|core\s?i[3579]|radeon)\s?\d+/i,
              // Samsung/LG модели: S24, OLED55, Q90T
              /\b(galaxy\s?[sazn]\d+|oled\d+|qled\d+|[qn]\d{2}[a-z]?)\b/i,
              // Авто: X5, GLE350, Camry
              /\b(x[1-7]|gle\d+|glc\d+|e\d{3}|[a-z]\d{3})\b/i,
              // Версии и модели с точками: v2.0, 3.5.1
              /\bv?\d+\.\d+(\.\d+)?\b/,
              // Артикулы с дефисом/подчеркиванием: ABC-123, XYZ_456
              /\b[a-z]{2,}[-_]\d{2,}\b/i,
              // Модели с годом: 2023, 2024
              /\b20[2-3]\d\b/,
              // Бытовая техника Bosch, Siemens: WAW28740, WM14N0K4
              /\b[a-z]{2,}\d{2}[a-z]\d{3,}\b/i,
            ];

            // Проверяем паттерны моделей
            const hasModelPattern = modelPatterns.some((pattern) =>
              pattern.test(keyword),
            );
            if (hasModelPattern) return true;

            // Дополнительные эвристики
            const words = keyword.split(/\s+/);

            // Проверяем наличие слов с буквами и цифрами
            const hasAlphanumeric = words.some(
              (word) => /[a-zа-яіїє]/i.test(word) && /\d/.test(word), // i18n-allow-cyrillic: дані: одиниці виміру/регулярки/назва кластера з БД
            );

            // Специфичность: низкая частотность + наличие цифр
            const isSpecific = volume < 100 && /\d/.test(keyword);

            // Длинный хвост с брендом
            const brandKeywords = [
              "samsung",
              "apple",
              "xiaomi",
              "huawei",
              "sony",
              "lg",
              "bosch",
              "mercedes",
              "bmw",
              "audi",
              "toyota",
              "honda",
              "volkswagen",
              "nike",
              "adidas",
              "puma",
              "reebok",
              "new balance",
            ];
            const hasBrand = brandKeywords.some((brand) =>
              keyword.includes(brand),
            );
            const hasNumbers = /\d/.test(keyword);

            // Комбинированная проверка
            return (
              (hasAlphanumeric && !keyword.includes(" ")) ||
              (isSpecific && words.length >= 2) ||
              (hasBrand && hasNumbers)
            );
          });
        }
      }
    } else {
      // Используем старые фильтры для обратной совместимости
      if (filters.value.minVolume !== null) {
        result = result.filter(
          (kw) => (kw.volume || 0) >= filters.value.minVolume!,
        );
      }
      if (filters.value.maxVolume !== null) {
        result = result.filter(
          (kw) => (kw.volume || 0) <= filters.value.maxVolume!,
        );
      }

      if (filters.value.minLength !== null) {
        result = result.filter(
          (kw) => kw.keyword.length >= filters.value.minLength!,
        );
      }

      if (filters.value.containsWords) {
        const words = filters.value.containsWords
          .split(",")
          .map((w) => w.trim().toLowerCase())
          .filter((w) => w);
        if (words.length > 0) {
          result = result.filter((kw) => {
            const kwLower = kw.keyword.toLowerCase();
            return words.some((word) => kwLower.includes(word));
          });
        }
      }

      if (filters.value.excludeWords) {
        const words = filters.value.excludeWords
          .split(",")
          .map((w) => w.trim().toLowerCase())
          .filter((w) => w);
        if (words.length > 0) {
          result = result.filter((kw) => {
            const kwLower = kw.keyword.toLowerCase();
            return !words.some((word) => kwLower.includes(word));
          });
        }
      }
    }

    // Сортировка
    result.sort((a, b) => {
      let valueA: any, valueB: any;

      switch (filters.value.sortBy) {
        case "keyword":
          valueA = a.keyword.toLowerCase();
          valueB = b.keyword.toLowerCase();
          break;
        case "volume":
          // Treat null as -1 so it appears at the end when sorting desc
          valueA = a.volume === null ? -1 : a.volume;
          valueB = b.volume === null ? -1 : b.volume;
          break;
        case "created_at":
          valueA = new Date(a.created_at).getTime();
          valueB = new Date(b.created_at).getTime();
          break;
      }

      if (filters.value.sortOrder === "asc") {
        return valueA > valueB ? 1 : -1;
      } else {
        return valueA < valueB ? 1 : -1;
      }
    });

    return result;
  });

  // Выбранные ключевые слова
  const selectedKeywords = computed(() => {
    return filteredKeywords.value.filter((kw) =>
      selectedKeywordIds.value.has(kw.id),
    );
  });

  // Общий объем выбранных ключей
  const totalVolume = computed(() => {
    return selectedKeywords.value.reduce(
      (sum, kw) => sum + (kw.volume || 0),
      0,
    );
  });

  // Проверка активных фильтров
  const hasActiveFilters = computed(() => {
    // Проверяем групповые фильтры
    if (
      activeGroupFilters.value &&
      activeGroupFilters.value.children.length > 0
    ) {
      return true;
    }

    // Проверяем старые фильтры
    if (!activeFilters.value) return false;

    const af = activeFilters.value;
    return (
      af.minVolume !== null ||
      af.maxVolume !== null ||
      af.minLength !== null ||
      af.maxLength !== null ||
      af.containsWords !== "" ||
      af.excludeWords !== "" ||
      af.regex !== "" ||
      af.excludeNumbers ||
      af.excludeSpecialChars ||
      af.excludeDuplicates
    );
  });

  // Получение списка активных фильтров в виде массива условий
  const advancedFilters = computed(() => {
    const filters: any[] = [];

    // Добавляем групповые фильтры
    if (
      activeGroupFilters.value &&
      activeGroupFilters.value.children.length > 0
    ) {
      const extractConditions = (item: FilterCondition | FilterGroup): void => {
        if ("type" in item && item.type === "group") {
          item.children.forEach(extractConditions);
        } else {
          filters.push(item);
        }
      };
      activeGroupFilters.value.children.forEach(extractConditions);
    }

    // Добавляем старые фильтры в новом формате
    if (activeFilters.value) {
      const af = activeFilters.value;

      if (af.minVolume !== null) {
        filters.push({
          field: "volume",
          operator: "greater",
          value: af.minVolume.toString(),
        });
      }

      if (af.maxVolume !== null) {
        filters.push({
          field: "volume",
          operator: "less",
          value: af.maxVolume.toString(),
        });
      }

      if (af.containsWords) {
        filters.push({
          field: "keyword",
          operator: "contains",
          value: af.containsWords,
        });
      }

      if (af.excludeWords) {
        filters.push({
          field: "keyword",
          operator: "not_contains",
          value: af.excludeWords,
        });
      }

      if (af.regex) {
        filters.push({
          field: "keyword",
          operator: "matches_regex",
          value: af.regex,
        });
      }
    }

    return filters;
  });

  // Функция для оценки групповых фильтров
  function evaluateFilterGroup(keyword: Keyword, group: FilterGroup): boolean {
    if (group.children.length === 0) return true;

    const results = group.children.map((child) => {
      if ("type" in child && child.type === "group") {
        return evaluateFilterGroup(keyword, child);
      } else {
        return evaluateFilterCondition(keyword, child);
      }
    });

    if (group.operator === "AND") {
      return results.every((r) => r);
    } else {
      return results.some((r) => r);
    }
  }

  // Функция для оценки отдельных условий
  function evaluateFilterCondition(
    keyword: Keyword,
    condition: FilterCondition,
  ): boolean {
    // Handle value based on field type
    let value: string = "";
    if (condition.field === "volume") {
      // For volume, keep as is (might be number or string)
      value =
        condition.value !== null && condition.value !== undefined
          ? String(condition.value)
          : "";
    } else {
      // For phrase filters, ensure it's a string and trim
      value = String(condition.value || "").trim();
    }

    if (condition.field === "phrase") {
      const keywordLower = keyword.keyword.toLowerCase();
      const valueLower = value.toLowerCase();
      const words = keywordLower.split(/\s+/).filter((w) => w.length > 0);

      switch (condition.operator) {
        // Текстовые условия
        case "contains":
          return keywordLower.includes(valueLower);
        case "not_contains":
          return !keywordLower.includes(valueLower);
        case "contains_separate_word":
          // Экранируем специальные символы регулярных выражений
          const escapedValue = valueLower.replace(
            /[.*+?^${}()|[\]\\]/g,
            "\\$&",
          );
          // Используем более универсальный паттерн для границ слова, который работает с кириллицей
          // (?<![а-яёa-z0-9]) - не предшествует буква или цифра
          // (?![а-яёa-z0-9]) - не следует буква или цифра
          const wordBoundaryRegex = new RegExp(
            `(?<![а-яёa-z0-9])${escapedValue}(?![а-яёa-z0-9])`, // i18n-allow-cyrillic: дані: одиниці виміру/регулярки/назва кластера з БД
            "i",
          );
          return wordBoundaryRegex.test(keyword.keyword);
        case "not_contains_separate_word":
          // Экранируем специальные символы регулярных выражений
          const escapedValueNot = valueLower.replace(
            /[.*+?^${}()|[\]\\]/g,
            "\\$&",
          );
          // Используем более универсальный паттерн для границ слова, который работает с кириллицей
          const notWordBoundaryRegex = new RegExp(
            `(?<![а-яёa-z0-9])${escapedValueNot}(?![а-яёa-z0-9])`, // i18n-allow-cyrillic: дані: одиниці виміру/регулярки/назва кластера з БД
            "i",
          );
          return !notWordBoundaryRegex.test(keyword.keyword);
        case "equals":
          return keywordLower === valueLower;
        case "not_equals":
          return keywordLower !== valueLower;
        case "starts_with":
          return keywordLower.startsWith(valueLower);
        case "ends_with":
          return keywordLower.endsWith(valueLower);

        // Регулярные выражения
        case "matches_regex":
          try {
            const regex = new RegExp(value, "i");
            return regex.test(keyword.keyword);
          } catch {
            return false;
          }
        case "not_matches_regex":
          try {
            const regex = new RegExp(value, "i");
            return !regex.test(keyword.keyword);
          } catch {
            return true;
          }

        // Повторы слов
        case "has_duplicates":
          const wordCounts = words.reduce(
            (acc, word) => {
              acc[word] = (acc[word] || 0) + 1;
              return acc;
            },
            {} as Record<string, number>,
          );
          return Object.values(wordCounts).some((count) => count > 1);
        case "has_specified_duplicates":
          if (!value) return true;
          const specifiedWords = value
            .toLowerCase()
            .split(",")
            .map((w) => w.trim())
            .filter((w) => w);
          // Проверяем, что НИ ОДНО из указанных слов не повторяется
          return !specifiedWords.some((word) => {
            const count = words.filter((w) => w === word).length;
            return count > 1;
          });

        // Количество слов
        case "words_exactly_2":
          return words.length >= 2;
        case "words_exactly_3":
          return words.length >= 3;
        case "words_exactly_4":
          return words.length >= 4;
        case "words_exactly_5":
          return words.length >= 5;
        case "words_exactly_6":
          return words.length >= 6;
        case "words_exactly_7":
          return words.length >= 7;
        case "words_exactly_8":
          return words.length >= 8;
        case "words_max_1":
          return words.length <= 1;
        case "words_max_2":
          return words.length <= 2;
        case "words_max_3":
          return words.length <= 3;
        case "words_max_4":
          return words.length <= 4;
        case "words_max_5":
          return words.length <= 5;
        case "words_max_6":
          return words.length <= 6;
        case "words_max_7":
          return words.length <= 7;
        case "words_max_8":
          return words.length <= 8;

        // Символы
        case "has_latin":
          return /[a-zA-Z]/.test(keyword.keyword);
        case "no_latin":
          return !/[a-zA-Z]/.test(keyword.keyword);
        case "has_digits":
          return /\d/.test(keyword.keyword);
        case "no_digits":
          return !/\d/.test(keyword.keyword);
        case "has_cyrillic":
          return /[А-яёЁіїєґІЇЄҐ]/.test(keyword.keyword); // i18n-allow-cyrillic: дані: одиниці виміру/регулярки/назва кластера з БД
        case "no_cyrillic":
          return !/[А-яёЁіїєґІЇЄҐ]/.test(keyword.keyword); // i18n-allow-cyrillic: дані: одиниці виміру/регулярки/назва кластера з БД

        // Языковые фильтры
        case "is_ukrainian":
          return languageDetector.matchesLanguageFilter(
            keyword.keyword,
            "is_ukrainian",
          );
        case "is_russian":
          return languageDetector.matchesLanguageFilter(
            keyword.keyword,
            "is_russian",
          );

        // Товарні ключі: артикули, моделі, бренди.
        // product_alphanumeric і product_smart були двома фільтрами для
        // однієї задачі — обʼєднані в product_keys. Старі значення лишені
        // синонімами, щоб не ламати вже налаштовані користувачем умови.
        case "product_keys":
        case "product_alphanumeric":
        case "product_smart":
          return isProductKeyword(keyword.keyword);

        default:
          return true;
      }
    } else if (condition.field === "volume") {
      const keywordVolume = keyword.volume;

      // Особый случай - без указания частотности
      if (condition.operator === "no_volume") {
        return keywordVolume === null || keywordVolume === undefined;
      }

      const filterValue = parseFloat(value);
      if (isNaN(filterValue)) return true;

      // Если у ключевого слова нет частотности, считаем её равной 0
      const volume = keywordVolume || 0;

      switch (condition.operator) {
        case "equals":
          return volume === filterValue;
        case "greater":
          return volume > filterValue;
        case "less":
          return volume < filterValue;
        case "greater_or_equal":
          return volume >= filterValue;
        case "less_or_equal":
          return volume <= filterValue;
        default:
          return true;
      }
    }

    return true;
  }

  // Actions
  async function loadKeywords(
    pId: string,
    force = false,
    loadClustersData = true,
  ) {
    console.log("[loadKeywords] CALLED", {
      pId,
      force,
      loadClustersData,
      currentProjectId: projectId.value,
      keywordsCount: keywords.value.length,
    });

    // ВАЖНО: Если проект изменился - всегда принудительно перезагружаем
    const projectChanged = projectId.value !== pId;
    if (projectChanged) {
      console.log("[loadKeywords] PROJECT CHANGED - forcing reload", {
        oldProjectId: projectId.value,
        newProjectId: pId,
      });
      // Очищаем данные старого проекта перед загрузкой нового
      keywords.value = [];
      clusters.value = [];
      stats.value = { total: 0, cleaned: 0, excluded: 0, clustered: 0 };
    }

    // Загружаем только если это новый проект или принудительно
    if (
      !force &&
      !projectChanged &&
      projectId.value === pId &&
      keywords.value.length > 0
    ) {
      console.log("[loadKeywords] SKIPPED - already loaded");
      keywordsLogger.log("Skipping keywords load - already loaded");
      return;
    }

    console.log("[loadKeywords] PROCEEDING with load");
    keywordsLogger.log("Loading keywords for project:", pId);
    projectId.value = pId;
    isLoading.value = true;

    try {
      // Загружаем информацию о проекте
      const projectResponse = await api.get(`/api/projects/${pId}`);
      project.value = projectResponse.data;
      keywordsLogger.log("Project loaded:", project.value);

      // Один запрос для получения всех ключевых слов
      const response = await api.get(`/api/projects/${pId}/keywords`);
      keywordsLogger.log("Keywords API response:", {
        status: response.status,
        hasData: !!response.data,
        keywordsCount: response.data.keywords?.length || 0,
        firstKeyword: response.data.keywords?.[0],
        // Debug cluster_id
        keywordsWithClusterId:
          response.data.keywords?.filter((k: any) => k.cluster_id).length || 0,
        sampleKeywordWithCluster: response.data.keywords?.find(
          (k: any) => k.cluster_id,
        ),
      });

      keywords.value = response.data.keywords || [];
      stats.value = response.data.stats || {
        total: keywords.value.length,
        cleaned: 0,
        excluded: 0,
        clustered: 0,
      };

      keywordsLogger.log(
        "Keywords loaded:",
        keywords.value.length,
        "Current tab:",
        currentTab.value,
      );
      keywordsLogger.log("Stats:", stats.value);
      keywordsLogger.log(
        "Active keywords:",
        keywords.value.filter((k) => k.status === "active").length,
      );
      keywordsLogger.log(
        "Keywords statuses:",
        keywords.value
          .slice(0, 5)
          .map((k) => ({ id: k.id, keyword: k.keyword, status: k.status })),
      );
      keywordsLogger.log("Filtered by tab count:", filteredByTab.value.length);
      keywordsLogger.log(
        "Filtered keywords count:",
        filteredKeywords.value.length,
      );

      // Загружаем кластеры отдельно
      if (loadClustersData) {
        try {
          keywordsLogger.log("Loading clusters for project:", pId);
          // Добавляем timestamp для предотвращения кэширования
          const clustersResponse = await api.get(
            `/api/projects/${pId}/clusters?t=${Date.now()}`,
          );
          keywordsLogger.log("Clusters API response:", clustersResponse.data);
          keywordsLogger.log(
            "Raw clusters from API:",
            JSON.stringify(clustersResponse.data.clusters.slice(0, 3), null, 2),
          );

          // Логируем порядок кластеров из API
          keywordsLogger.log(
            "Clusters order from API:",
            clustersResponse.data.clusters.map((c: any, index: number) => ({
              index,
              id: c.id,
              name: c.name,
              sort_order: c.sort_order,
            })),
          );

          clusters.value = clustersResponse.data.clusters || [];
          keywordsLogger.log("Clusters loaded:", clusters.value.length);

          // Диагностика для kingston m 2 1tb
          const findKingstonCluster = (clusters: Cluster[]): Cluster | null => {
            for (const cluster of clusters) {
              if (cluster.name === "kingston m 2 1tb") {
                return cluster;
              }
              if (cluster.children && cluster.children.length > 0) {
                const found = findKingstonCluster(cluster.children);
                if (found) return found;
              }
            }
            return null;
          };

          const kingstonCluster = findKingstonCluster(clusters.value);
          if (kingstonCluster) {
            // DEBUG kingston m 2 1tb - use keywordsLogger if debugging needed
          }
          keywordsLogger.log(
            "Clusters with children:",
            clusters.value
              .filter((c) => c.children && c.children.length > 0)
              .map((c) => ({
                id: c.id,
                name: c.name,
                childrenCount: c.children?.length,
                children: c.children?.map((ch) => ({
                  id: ch.id,
                  name: ch.name,
                  childrenCount: ch.children?.length,
                })),
              })),
          );

          // Найдем кластер с id 6651
          // Временная отладка удалена - использовать keywordsLogger если нужна отладка

          // Диагностика структуры кластеров удалена - использовать keywordsLogger если нужна

          // Детальная диагностика кластеров удалена - использовать keywordsLogger если нужна

          // Вызываем обновление для полной синхронизации
          updateClustersWithKeywords();

          // Проверяем наличие кластеров в БД
          if (clusters.value.length === 0) {
            keywordsLogger.warn(
              "No clusters found in database for project:",
              pId,
            );
          }
        } catch (error) {
          errorsLogger.error("Error loading clusters:", error);
          keywordsLogger.log("No clusters yet");
          clusters.value = [];
        }
      }
    } catch (error) {
      errorsLogger.error("Error loading keywords:", error);
      toast.error(t("toasts.keywordsLoadErrorStore"));
    } finally {
      isLoading.value = false;
    }
  }

  // Обновить только статистику без перезагрузки всех данных
  async function refreshStats() {
    if (!projectId.value) return;

    try {
      const response = await api.get(
        `/api/projects/${projectId.value}/keywords?stats_only=true`,
      );
      stats.value = response.data.stats;
    } catch (error) {
      errorsLogger.error("Error refreshing stats:", error);
    }
  }

  // Добавить новые ключевые слова в локальный store
  function addKeywords(newKeywords: Keyword[]) {
    keywords.value = [...keywords.value, ...newKeywords];
    refreshStats();
  }

  // Обновить существующие ключевые слова
  function updateKeywords(updatedKeywords: Keyword[]) {
    const updatedMap = new Map(updatedKeywords.map((k) => [k.id, k]));

    keywords.value = keywords.value.map((k) =>
      updatedMap.has(k.id) ? updatedMap.get(k.id)! : k,
    );
    refreshStats();
  }

  // Удалить ключевые слова (изменить статус на excluded)
  async function removeKeywords(keywordIds: number[], skipHistory = false) {
    if (!projectId.value || keywordIds.length === 0) return;

    const idsSet = new Set(keywordIds);

    // Сохраняем информацию для отката
    const keywordsToRemove = keywords.value.filter((k) => idsSet.has(k.id));
    const removedKeywordsData = keywordsToRemove.map((k) => ({
      id: k.id,
      keyword: k.keyword,
      clusterId: k.cluster_id,
    }));

    try {
      // Сначала получаем ID кластеров, из которых удаляем ключевые слова
      const affectedClusterIds = new Set<number>();
      keywords.value.forEach((k) => {
        if (idsSet.has(k.id) && k.cluster_id) {
          affectedClusterIds.add(k.cluster_id);
        }
      });

      // Делаем API запрос для сохранения на сервере
      await api.post(
        `/api/projects/${projectId.value}/cleaning/confirm-minus-words`,
        {
          keyword_ids: keywordIds,
          excluded_ids: [], // Пустой массив, так как мы хотим удалить все выбранные
        },
      );

      // Обновляем статус ключевых слов на excluded локально и отвязываем от кластеров
      keywords.value = keywords.value.map((k) => {
        if (idsSet.has(k.id)) {
          return {
            ...k,
            status: "excluded" as const,
            cleaning_status: "excluded" as const,
            excluded_by_filter: "manual_deletion",
            cluster_id: null, // Отвязываем от кластера
          };
        }
        return k;
      });

      // Проверяем и удаляем пустые кластеры
      if (affectedClusterIds.size > 0) {
        const clustersToRemove: number[] = [];

        affectedClusterIds.forEach((clusterId) => {
          // Проверяем, остались ли активные ключевые слова в кластере
          const hasActiveKeywords = keywords.value.some(
            (k) => k.cluster_id === clusterId && k.status === "active",
          );

          if (!hasActiveKeywords) {
            clustersToRemove.push(clusterId);
          }
        });

        // Удаляем пустые кластеры из списка
        if (clustersToRemove.length > 0) {
          clusters.value = clusters.value.filter(
            (c) => !clustersToRemove.includes(c.id),
          );
          keywordsLogger.log("Removed empty clusters:", clustersToRemove);
        }
      }

      // Очищаем выделение для удаленных ключевых слов
      selectedKeywordIds.value = new Set(
        Array.from(selectedKeywordIds.value).filter((id) => !idsSet.has(id)),
      );

      // Обновляем статистику локально
      const excludedCount = keywords.value.filter(
        (k) => k.status === "excluded",
      ).length;
      stats.value = {
        ...stats.value,
        excluded: excludedCount,
      };

      refreshStats();

      // Добавляем действие в историю
      if (!skipHistory && removedKeywordsData.length > 0) {
        const action: ActionData = {
          type: "keywords_move",
          description: "", // Keep empty for backward compatibility
          descriptionKey:
            removedKeywordsData.length === 1
              ? "undoRedo.actions.deleteKeyword"
              : "undoRedo.actions.deleteKeywords",
          descriptionParams:
            removedKeywordsData.length === 1
              ? {}
              : { count: removedKeywordsData.length },
          timestamp: Date.now(),
          payload: {
            keywordIds: keywordIds,
            targetClusterId: -1, // Специальный ID для корзины
          },
        };
        addAction(action);
      }
    } catch (error) {
      errorsLogger.error("Error removing keywords:", error);
      toast.error(t("toasts.keywordsDeleteErrorStore"));
      throw error;
    }
  }

  // Установить текущую вкладку
  function setCurrentTab(tab: "keywords" | "cleaning" | "clusters") {
    currentTab.value = tab;
    // Не сбрасываем выделение при переключении вкладок
  }

  // Выделение ключевых слов
  function selectAllKeywords() {
    filteredKeywords.value.forEach((kw) => {
      selectedKeywordIds.value.add(kw.id);
    });
  }

  function deselectAllKeywords() {
    selectedKeywordIds.value.clear();
  }

  function toggleKeywordSelection(keyword: Keyword) {
    if (selectedKeywordIds.value.has(keyword.id)) {
      selectedKeywordIds.value.delete(keyword.id);
    } else {
      selectedKeywordIds.value.add(keyword.id);
    }
  }

  function isKeywordSelected(keyword: Keyword): boolean {
    return selectedKeywordIds.value.has(keyword.id);
  }

  // Фильтры
  function updateFilters(newFilters: Partial<typeof filters.value>) {
    Object.assign(filters.value, newFilters);
  }

  function clearFilters() {
    filters.value = {
      search: "",
      minVolume: null,
      maxVolume: null,
      minLength: null,
      containsWords: "",
      excludeWords: "",
      sortBy: "volume",
      sortOrder: "desc",
      showExcluded: false,
    };
  }

  // Новые методы для расширенных фильтров
  function applyAdvancedFilters(advFilters: AdvancedFilters) {
    activeFilters.value = advFilters;
  }

  function clearAdvancedFilters() {
    activeFilters.value = null;
    activeGroupFilters.value = null;
  }

  // Методы для быстрого фильтра
  function applyQuickFilter(searchText: string) {
    quickFilterText.value = searchText;
  }

  function clearQuickFilter() {
    quickFilterText.value = "";
  }

  // Legacy method for backward compatibility
  function applyFilters(newFilters: Partial<typeof filters.value>) {
    updateFilters(newFilters);
  }

  // Новый метод для применения групповых фильтров
  function applyAdvancedGroupFilters(groupFilter: FilterGroup) {
    activeGroupFilters.value = groupFilter;
    // Очищаем старые фильтры при применении новых
    activeFilters.value = null;
  }

  // API методы для каждой вкладки
  async function collectKeywords(data: any) {
    if (!projectId.value) return;

    try {
      const response = await api.post(
        `/api/projects/${projectId.value}/keywords/collect`,
        data,
      );
      if (response.data.keywords) {
        addKeywords(response.data.keywords);
      }
      return response.data;
    } catch (error) {
      errorsLogger.error("Error collecting keywords:", error);
      throw error;
    }
  }

  async function applyCleaningFilters(keywordIds: number[], filters: any) {
    if (!projectId.value) return;

    try {
      const response = await api.post(
        `/api/projects/${projectId.value}/keywords/apply-filters`,
        {
          keyword_ids: keywordIds,
          filters,
        },
      );
      if (response.data.updated_keywords) {
        updateKeywords(response.data.updated_keywords);
      }
      return response.data;
    } catch (error) {
      errorsLogger.error("Error applying filters:", error);
      throw error;
    }
  }

  async function createClusters(mode: string, params: any) {
    if (!projectId.value) return;

    try {
      const response = await api.post(
        `/api/projects/${projectId.value}/clusters`,
        {
          mode,
          ...params,
        },
      );

      // Обновляем кластеры и ключевые слова
      if (response.data.clusters) {
        keywordsLogger.log(
          "Clusters received from API:",
          response.data.clusters.length,
        );
        if (response.data.clusters.length > 0) {
          keywordsLogger.log("First cluster from API:", {
            id: response.data.clusters[0].id,
            name: response.data.clusters[0].name,
            keywords: response.data.clusters[0].keywords,
            keywords_count: response.data.clusters[0].keywords_count,
          });
        }
        clusters.value = response.data.clusters;
      }

      // Перезагружаем ТОЛЬКО ключевые слова чтобы получить обновленные cluster_id
      // НЕ перезагружаем кластеры, так как они уже загружены с keywords
      const forceReload = true;
      await loadKeywords(projectId.value, forceReload, false); // false = не загружать кластеры

      // Синхронизируем кластеры с обновленными ключевыми словами
      syncClustersWithKeywords();

      return response.data;
    } catch (error) {
      errorsLogger.error("Error creating clusters:", error);
      throw error;
    }
  }

  async function deleteAllClusters(projectIdParam: string | number) {
    const projectIdToUse = projectIdParam || projectId.value;
    if (!projectIdToUse) return;

    try {
      await api.delete(`/api/projects/${projectIdToUse}/clusters`);

      // Очищаем локальное состояние кластеров
      clusters.value = [];

      // Обнуляем cluster_id для всех ключевых слов
      keywords.value.forEach((keyword) => {
        keyword.cluster_id = null;
      });

      // Обновляем статистику
      updateStats();

      toast.success(t("toasts.allClustersCleared"));
      return true;
    } catch (error) {
      errorsLogger.error("Error deleting all clusters:", error);
      toast.error(t("toasts.clustersClearError"));
      throw error;
    }
  }

  // Переименование кластера
  async function renameCluster(
    clusterId: number,
    newName: string,
    skipHistory = false,
  ) {
    if (!projectId.value) return;

    // Сохраняем старое имя для отката
    const cluster = clusters.value.find((c) => c.id === clusterId);
    const oldName = cluster?.name || "";

    isProcessingClusters.value = true;
    processingOperation.value = "rename";
    try {
      const response = await api.put(
        `/api/projects/${projectId.value}/clusters/${clusterId}`,
        {
          name: newName,
        },
      );

      if (response.data.success) {
        // Обновляем кластер в локальном состоянии
        const index = clusters.value.findIndex((c) => c.id === clusterId);
        if (index !== -1) {
          clusters.value[index].name = newName;
        }

        // Добавляем действие в историю только если это не откат
        if (!skipHistory && oldName !== newName) {
          const action: ActionData = {
            type: "cluster_rename",
            description: "", // Keep empty for backward compatibility
            descriptionKey: "undoRedo.actions.renameCluster",
            descriptionParams: { oldName, newName },
            timestamp: Date.now(),
            payload: {
              clusterId,
              oldName,
              newName,
            },
          };
          addAction(action);
        }

        if (!skipHistory) {
          toast.success(t("toasts.clusterRenameSuccess"));
        }
      }
    } catch (error) {
      errorsLogger.error("Error renaming cluster:", error);
      if (!skipHistory) {
        toast.error(t("toasts.clusterRenameFailed"));
      }
      throw error;
    } finally {
      isProcessingClusters.value = false;
      processingOperation.value = null;
    }
  }

  // Удаление отдельного кластера
  async function deleteCluster(clusterId: number, skipHistory = false) {
    if (!projectId.value) return;

    isProcessingClusters.value = true;
    processingOperation.value = "delete";
    try {
      // Сохраняем данные кластера перед удалением (для undo)
      const clusterToDelete = clusters.value.find((c) => c.id === clusterId);
      const clusterName = clusterToDelete?.name || "";
      const parentId = clusterToDelete?.parent_id || null;
      const affectedKeywordIds = keywords.value
        .filter((k) => k.cluster_id === clusterId)
        .map((k) => k.id);

      await api.delete(
        `/api/projects/${projectId.value}/clusters/${clusterId}`,
      );

      // Удаляем кластер из локального состояния
      clusters.value = clusters.value.filter((c) => c.id !== clusterId);

      // Обновляем cluster_id для ключевых слов, которые были в удаленном кластере
      keywords.value.forEach((keyword) => {
        if (keyword.cluster_id === clusterId) {
          keyword.cluster_id = null;
        }
      });

      // Обновляем статистику
      updateStats();

      // Добавляем в историю для undo
      if (!skipHistory) {
        const action: ActionData = {
          type: "cluster_delete",
          description: "",
          descriptionKey: "undoRedo.actions.deleteCluster",
          descriptionParams: { name: clusterName },
          timestamp: Date.now(),
          payload: {
            clusterId,
            clusterName,
            oldParentId: parentId,
            keywordIds: affectedKeywordIds,
          },
        };
        addAction(action);
      }

      if (!skipHistory) {
        toast.success(t("toasts.clusterDeleted"));
      }
    } catch (error) {
      errorsLogger.error("Error deleting cluster:", error);
      toast.error(t("toasts.clusterDeleteFailed"));
      throw error;
    } finally {
      isProcessingClusters.value = false;
      processingOperation.value = null;
    }
  }

  // Создание ручного кластера (используется для undo удаления)
  async function createManualCluster(
    name: string,
    keywordIds: number[] = [],
    parentId: number | null = null,
    skipHistory = false,
  ) {
    if (!projectId.value) return;

    isProcessingClusters.value = true;
    processingOperation.value = "rename";
    try {
      const response = await api.post(
        `/api/projects/${projectId.value}/clusters/manual`,
        {
          name,
          keyword_ids: keywordIds,
          parent_id: parentId,
        },
      );

      if (response.data.success) {
        // Перезагружаем кластеры
        const clustersResponse = await api.get(
          `/api/projects/${projectId.value}/clusters`,
        );
        clusters.value = clustersResponse.data.clusters || [];

        // Обновляем keyword.cluster_id для привязанных ключевых слов
        const newClusterId = response.data.cluster?.id;
        if (newClusterId && keywordIds.length > 0) {
          keywords.value.forEach((keyword) => {
            if (keywordIds.includes(keyword.id)) {
              keyword.cluster_id = newClusterId;
            }
          });
        }

        updateStats();

        if (!skipHistory) {
          toast.success(t("toasts.clusterCreatedSuccess"));
        }

        return response.data.cluster;
      }
    } catch (error) {
      errorsLogger.error("Error creating manual cluster:", error);
      toast.error(t("toasts.clusterCreateErrorGeneric"));
      throw error;
    } finally {
      isProcessingClusters.value = false;
      processingOperation.value = null;
    }
  }

  // Перемещение ключевых слов в другой кластер
  async function moveKeywordsToCluster(
    keywordIds: number[],
    targetClusterId: number,
    sourceClusterId?: number,
  ) {
    if (!projectId.value) return;

    isProcessingClusters.value = true;
    processingOperation.value = "move-keywords";
    try {
      // Получаем полные данные ключевых слов для отправки
      const keywordsToMove = keywords.value
        .filter((k) => keywordIds.includes(k.id))
        .map((k) => ({
          text: k.keyword,
          volume: k.volume || 0,
        }));

      const url = `/api/projects/${projectId.value}/clusters/${targetClusterId}/add-keywords`;
      const payload = {
        keywords: keywordsToMove,
        source_cluster_id: sourceClusterId,
      };

      keywordsLogger.log("API Request:", { url, payload });

      const response = await api.post(url, payload);

      if (response.data.success) {
        // Частичное обновление данных вместо полной перезагрузки
        const {
          target_cluster,
          source_cluster,
          source_cluster_deleted,
          moved_keyword_ids,
        } = response.data;

        // Обновляем cluster_id для перемещенных ключевых слов
        keywords.value.forEach((keyword) => {
          if (moved_keyword_ids.includes(keyword.id)) {
            keyword.cluster_id = targetClusterId;
          }
        });

        // Обновляем целевой кластер
        const updateClusterInTree = (clusterId: number, updatedData: any) => {
          for (let i = 0; i < clusters.value.length; i++) {
            if (clusters.value[i].id === clusterId) {
              clusters.value[i] = {
                ...clusters.value[i],
                keywords_count: updatedData.keywords_count,
                keywords: updatedData.keywords,
                is_modified:
                  updatedData.is_modified ?? clusters.value[i].is_modified,
              };
              return true;
            }
            // Проверяем дочерние кластеры
            if (clusters.value[i].children) {
              for (let j = 0; j < clusters.value[i].children.length; j++) {
                if (clusters.value[i].children[j].id === clusterId) {
                  clusters.value[i].children[j] = {
                    ...clusters.value[i].children[j],
                    keywords_count: updatedData.keywords_count,
                    keywords: updatedData.keywords,
                    is_modified:
                      updatedData.is_modified ??
                      clusters.value[i].children[j].is_modified,
                  };
                  return true;
                }
              }
            }
          }
          return false;
        };

        updateClusterInTree(target_cluster.id, target_cluster);

        // Обновляем или удаляем исходный кластер
        if (sourceClusterId) {
          if (source_cluster_deleted) {
            // Удаляем кластер из списка
            clusters.value = clusters.value.filter(
              (c) => c.id !== sourceClusterId,
            );
          } else if (source_cluster) {
            // Обновляем исходный кластер
            const sourceClusterIndex = clusters.value.findIndex(
              (c) => c.id === sourceClusterId,
            );
            if (sourceClusterIndex !== -1) {
              clusters.value[sourceClusterIndex] = {
                ...clusters.value[sourceClusterIndex],
                keywords_count: source_cluster.keywords_count,
                keywords: source_cluster.keywords,
                is_modified:
                  source_cluster.is_modified ??
                  clusters.value[sourceClusterIndex].is_modified,
              };
            }
          }
        }

        // Обновляем статистику
        updateStats();

        // Перезагружаем кластеры для обновления всех счетчиков
        try {
          const clustersResponse = await api.get(
            `/api/projects/${projectId.value}/clusters`,
          );
          if (clustersResponse.data.clusters) {
            clusters.value = clustersResponse.data.clusters;
            keywordsLogger.log("Clusters reloaded after move:", clusters.value);
          }
        } catch (error) {
          errorsLogger.error("Error reloading clusters:", error);
        }

        toast.success(t("toasts.keywordsMovedSuccess"));
      }
    } catch (error: any) {
      errorsLogger.error("Error moving keywords:", error);
      errorsLogger.error("Error response:", error.response?.data);

      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else if (error.response?.data?.errors) {
        const firstError = Object.values(error.response.data.errors)[0];
        toast.error(Array.isArray(firstError) ? firstError[0] : firstError);
      } else {
        toast.error(t("toasts.keywordsMoveFailedStore"));
      }
      throw error;
    } finally {
      isProcessingClusters.value = false;
      processingOperation.value = null;
    }
  }

  // Создание подкластера
  async function createSubcluster(
    parentClusterId: number,
    name: string,
    keywordIds?: number[],
  ) {
    if (!projectId.value) return;

    isProcessingClusters.value = true;
    processingOperation.value = "rename"; // Use rename for create subcluster as it's similar operation
    try {
      const response = await api.post(
        `/api/projects/${projectId.value}/clusters/${parentClusterId}/subcluster`,
        {
          name: name,
          keyword_ids: keywordIds || [],
        },
      );

      if (response.data.success) {
        // Перезагружаем кластеры
        const clustersResponse = await api.get(
          `/api/projects/${projectId.value}/clusters`,
        );
        keywordsLogger.log(
          "Clusters after subcluster creation:",
          clustersResponse.data,
        );
        clusters.value = clustersResponse.data.clusters || [];

        // Проверяем, есть ли созданный подкластер в дереве
        const findClusterInTree = (clusters: any[], id: number): any => {
          for (const cluster of clusters) {
            if (cluster.id === id) return cluster;
            if (cluster.children) {
              const found = findClusterInTree(cluster.children, id);
              if (found) return found;
            }
          }
          return null;
        };

        const createdSubcluster = findClusterInTree(
          clusters.value,
          response.data.cluster.id,
        );
        keywordsLogger.log(
          "Created subcluster found in tree:",
          createdSubcluster,
        );

        toast.success(t("toasts.subclusterCreated"));
        return response.data.cluster;
      }
    } catch (error) {
      errorsLogger.error("Error creating subcluster:", error);
      toast.error(t("toasts.subclusterCreateFailed"));
      throw error;
    } finally {
      isProcessingClusters.value = false;
      processingOperation.value = null;
    }
  }

  function applyFilters(newFilters: {
    minVolume?: number | null;
    maxVolume?: number | null;
    minLength?: number | null;
    containsWords?: string;
    excludeWords?: string;
  }) {
    if (newFilters.minVolume !== undefined)
      filters.value.minVolume = newFilters.minVolume;
    if (newFilters.maxVolume !== undefined)
      filters.value.maxVolume = newFilters.maxVolume;
    if (newFilters.minLength !== undefined)
      filters.value.minLength = newFilters.minLength;
    if (newFilters.containsWords !== undefined)
      filters.value.containsWords = newFilters.containsWords;
    if (newFilters.excludeWords !== undefined)
      filters.value.excludeWords = newFilters.excludeWords;
  }

  // Функции для работы с удаленными ключевыми словами
  function setShowExcluded(show: boolean) {
    showExcludedMode.value = show;
  }

  async function restoreKeywords(keywordIds: number[], skipHistory = false) {
    const currentProjectId = projectId.value;
    console.log("[restoreKeywords] START", {
      currentProjectId,
      keywordIds: keywordIds.length,
      skipHistory,
    });
    if (!currentProjectId) {
      console.log("[restoreKeywords] ABORT - no projectId");
      return;
    }

    try {
      console.log("[restoreKeywords] Calling API to restore keywords");
      const response = await api.post(
        `/api/projects/${currentProjectId}/keywords/restore`,
        {
          keyword_ids: keywordIds,
        },
      );
      console.log("[restoreKeywords] API response:", response.status);

      // Перезагружаем все ключи с сервера вместо локального обновления
      // Это гарантирует что восстановленные ключи появятся в списке
      console.log("[restoreKeywords] Calling loadKeywords");
      console.log(
        "[restoreKeywords] BEFORE loadKeywords - keywords.value.length:",
        keywords.value.length,
        "active:",
        keywords.value.filter((k) => k.status === "active").length,
      );
      await loadKeywords(currentProjectId, true);
      console.log(
        "[restoreKeywords] AFTER loadKeywords - keywords.value.length:",
        keywords.value.length,
        "active:",
        keywords.value.filter((k) => k.status === "active").length,
      );
      console.log(
        "[restoreKeywords] filteredByTab.value.length:",
        filteredByTab.value.length,
      );
      console.log(
        "[restoreKeywords] filteredKeywords.value.length:",
        filteredKeywords.value.length,
      );

      // Очищаем выделение
      selectedKeywordIds.value = new Set(
        Array.from(selectedKeywordIds.value).filter(
          (id) => !keywordIds.includes(id),
        ),
      );

      // Показываем тост только если это не действие из истории (undo/redo)
      if (!skipHistory) {
        toast.success(t("toasts.keywordsRestored"));
      }
      console.log("[restoreKeywords] DONE");
    } catch (error) {
      console.error("[restoreKeywords] ERROR:", error);
      errorsLogger.error("Error restoring keywords:", error);
      if (!skipHistory) {
        toast.error(t("toasts.keywordsRestoreError"));
      }
    }
  }

  async function deleteKeywordsPermanently(keywordIds: number[]) {
    if (!projectId.value) return;

    try {
      const response = await api.delete(
        `/api/projects/${projectId.value}/keywords/bulk`,
        {
          data: {
            keyword_ids: keywordIds,
          },
        },
      );

      // Обновляем локальный стор
      const idsSet = new Set(keywordIds);
      keywords.value = keywords.value.filter((k) => !idsSet.has(k.id));

      // Очищаем выделение
      selectedKeywordIds.value = new Set(
        Array.from(selectedKeywordIds.value).filter((id) => !idsSet.has(id)),
      );

      // Проверяем, остались ли ключи в корзине
      // Если корзина пуста, автоматически переключаемся на "Все"
      const remainingExcluded = keywords.value.filter(
        (k) => k.status === "excluded",
      ).length;
      console.log(
        "[deleteKeywordsPermanently] remainingExcluded:",
        remainingExcluded,
        "showExcludedMode:",
        showExcludedMode.value,
      );
      if (remainingExcluded === 0 && showExcludedMode.value) {
        console.log(
          "[deleteKeywordsPermanently] Switching to All (trash is empty)",
        );
        showExcludedMode.value = false;
      }

      refreshStats();
      toast.success(
        response.data.message || t("toasts.keywordsDeletedPermanently"),
      );
    } catch (error) {
      errorsLogger.error("Error deleting keywords permanently:", error);
      toast.error(t("toasts.keywordsDeleteErrorFinal"));
    }
  }

  // Методы для работы с кластерами
  function setActiveClusterFilter(
    clusterId: number | string | (number | string)[] | null,
  ) {
    activeClusterFilter.value = clusterId;
  }

  function getClusterWithChildrenIds(clusterId: number): number[] {
    const ids: number[] = [clusterId];

    // Find the cluster in the tree structure
    const findClusterInTree = (
      clusters: Cluster[],
      targetId: number,
    ): Cluster | null => {
      for (const cluster of clusters) {
        if (cluster.id === targetId) {
          return cluster;
        }
        if (cluster.children && cluster.children.length > 0) {
          const found = findClusterInTree(cluster.children, targetId);
          if (found) return found;
        }
      }
      return null;
    };

    // Get all descendant IDs recursively
    const collectDescendantIds = (cluster: Cluster) => {
      if (cluster.children && cluster.children.length > 0) {
        cluster.children.forEach((child) => {
          ids.push(child.id);
          collectDescendantIds(child);
        });
      }
    };

    // Find the cluster and collect all its descendants
    const targetCluster = findClusterInTree(clusters.value, clusterId);
    if (targetCluster) {
      collectDescendantIds(targetCluster);
    }

    keywordsLogger.debug(
      "Getting cluster IDs for cluster:",
      clusterId,
      "All IDs (including children):",
      ids,
    );
    return ids;
  }

  // Построение дерева кластеров
  // Подсчитываем некластеризованные ключевые слова (только активные)
  const unclusteredCount = computed(() => {
    return keywords.value.filter((k) => !k.cluster_id && k.status === "active")
      .length;
  });

  const clustersTree = computed(() => {
    keywordsLogger.debug(
      "Building clusters tree from",
      clusters.value.length,
      "clusters",
    );

    // Сначала синхронизируем кластеры с ключевыми словами
    // чтобы получить актуальные keywords_count
    const keywordCountsByCluster = new Map<number, number>();
    keywords.value.forEach((keyword) => {
      if (keyword.cluster_id && keyword.status === "active") {
        keywordCountsByCluster.set(
          keyword.cluster_id,
          (keywordCountsByCluster.get(keyword.cluster_id) || 0) + 1,
        );
      }
    });

    // Сервер уже возвращает построенное дерево, нам нужно только обновить keywords_count
    // на основе актуальных данных из keywords
    const updateClusterCounts = (cluster: Cluster): Cluster => {
      const actualCount = keywordCountsByCluster.get(cluster.id) || 0;
      const updatedCluster = {
        ...cluster,
        keywords_count: actualCount,
        children: cluster.children
          ? cluster.children.map(updateClusterCounts)
          : [],
      };
      return updatedCluster;
    };

    // Обновляем счетчики во всем дереве
    const clustersCopy = clusters.value.map(updateClusterCounts);

    keywordsLogger.debug(
      "Clusters with updated counts:",
      clustersCopy.map((c) => ({
        id: c.id,
        name: c.name,
        parent_id: c.parent_id,
        keywords_count: c.keywords_count,
        is_unclustered: c.is_unclustered,
        has_children: c.children && c.children.length > 0,
      })),
    );

    // Сервер уже вернул дерево, поэтому используем все кластеры верхнего уровня
    const rootClusters = clustersCopy;

    // Рекурсивно подсчитываем общее количество ключевых слов включая подкластеры
    const updateTotalCounts = (cluster: Cluster): number => {
      let total = cluster.keywords_count || 0;
      if (cluster.children && cluster.children.length > 0) {
        cluster.children.forEach((child) => {
          total += updateTotalCounts(child);
        });
      }
      // Сохраняем общее количество в отдельное поле, не трогая keywords_count
      cluster.total_keywords_count = total;
      return total;
    };

    rootClusters.forEach(updateTotalCounts);

    keywordsLogger.debug(
      "Root clusters after total count update:",
      rootClusters.map((c) => ({
        id: c.id,
        name: c.name,
        keywords_count: c.keywords_count,
        total_keywords_count: c.total_keywords_count,
        is_unclustered: c.is_unclustered,
        children_count: c.children?.length || 0,
      })),
    );

    // Отделяем некластеризованные от обычных кластеров
    const unclusteredCluster = rootClusters.find((c) => {
      // Проверяем по флагу is_unclustered или по названию
      return (
        c.is_unclustered === true ||
        c.is_unclustered === 1 ||
        c.name === "Некластеризовані" || // i18n-allow-cyrillic: дані: одиниці виміру/регулярки/назва кластера з БД
        c.name === "Unclustered"
      );
    });

    let regularClusters = rootClusters.filter((c) => {
      return (
        c.is_unclustered !== true &&
        c.is_unclustered !== 1 &&
        c.name !== "Некластеризовані" && // i18n-allow-cyrillic: дані: одиниці виміру/регулярки/назва кластера з БД
        c.name !== "Unclustered"
      );
    });

    keywordsLogger.debug("Clusters tree sorting:", {
      total: rootClusters.length,
      unclustered: unclusteredCluster
        ? {
            id: unclusteredCluster.id,
            name: unclusteredCluster.name,
            is_unclustered: unclusteredCluster.is_unclustered,
            keywords_count: unclusteredCluster.keywords_count,
          }
        : null,
      regular_count: regularClusters.length,
      regular_clusters: regularClusters.map((c) => ({
        id: c.id,
        name: c.name,
        keywords_count: c.keywords_count,
      })),
    });

    // Порядок на всех уровнях дерева задаёт общее правило (utils/clusterOrder):
    // ручной порядок пользователя, а если его не задавали — частотность.
    // Раньше сортировался только корень, поэтому порядок подкластеров
    // не применялся никогда.
    regularClusters = sortClusterTree(regularClusters);

    keywordsLogger.debug(
      "Sorted regular clusters:",
      regularClusters.map((c) => ({
        name: c.name,
        keywords_count: c.keywords_count,
      })),
    );

    // Добавляем некластеризованные в конец, если они есть
    if (unclusteredCluster) {
      const result = [...regularClusters, unclusteredCluster];
      keywordsLogger.debug(
        "Final tree with unclustered at end:",
        result.map((c) => ({
          name: c.name,
          keywords_count: c.keywords_count,
          is_unclustered: c.is_unclustered,
        })),
      );
      return result;
    }

    return regularClusters;
  });

  // Обновление статистики на основе текущих данных
  function updateStats() {
    const activeKeywords = keywords.value.filter((k) => k.status === "active");
    const excludedKeywords = keywords.value.filter(
      (k) => k.status === "excluded",
    );
    const cleanedKeywords = keywords.value.filter(
      (k) => k.cleaning_status === "cleaned",
    );
    const clusteredKeywords = activeKeywords.filter(
      (k) => k.cluster_id !== null,
    );

    stats.value = {
      total: keywords.value.length,
      cleaned: cleanedKeywords.length,
      excluded: excludedKeywords.length,
      clustered: clusteredKeywords.length,
    };
  }

  // Синхронизация кластеров с ключевыми словами
  function syncClustersWithKeywords() {
    if (clusters.value.length === 0) return;

    // Создаем Map для быстрого поиска
    const clusterKeywords = new Map<number, Array<any>>();
    const clusterKeywordCounts = new Map<number, number>();

    // Группируем ключевые слова по кластерам
    keywords.value.forEach((keyword) => {
      if (keyword.cluster_id && keyword.status === "active") {
        if (!clusterKeywords.has(keyword.cluster_id)) {
          clusterKeywords.set(keyword.cluster_id, []);
        }
        clusterKeywords.get(keyword.cluster_id)!.push({
          id: keyword.id,
          text: keyword.keyword,
          volume: keyword.volume || 0,
        });
        clusterKeywordCounts.set(
          keyword.cluster_id,
          (clusterKeywordCounts.get(keyword.cluster_id) || 0) + 1,
        );
      }
    });

    // Рекурсивная функция для обновления кластеров на всех уровнях
    const updateClusterRecursively = (cluster: Cluster): Cluster => {
      const keywordsList = clusterKeywords.get(cluster.id) || [];
      const updatedCluster = {
        ...cluster,
        keywords: keywordsList,
        keywords_count: keywordsList.length,
      };

      // Рекурсивно обновляем детей, если они есть
      if (updatedCluster.children && updatedCluster.children.length > 0) {
        updatedCluster.children = updatedCluster.children.map((child) =>
          updateClusterRecursively(child),
        );
      }

      return updatedCluster;
    };

    // Синхронизируем поле keywords в кластерах с реальными данными на всех уровнях
    clusters.value = clusters.value.map((cluster) =>
      updateClusterRecursively(cluster),
    );
  }

  // Упрощенный метод - больше не нужно обновлять кластеры с ключевыми словами
  // так как теперь все работает через единую связь cluster_id
  function updateClustersWithKeywords() {
    keywordsLogger.debug(
      "updateClustersWithKeywords called - now using simplified cluster_id approach",
    );
    syncClustersWithKeywords();
  }

  // Алиас для loadKeywords для обратной совместимости
  async function loadClusters(pId: string) {
    keywordsLogger.debug("loadClusters called - redirecting to loadKeywords");
    return loadKeywords(pId, true, true);
  }

  /**
   * Обновляет только дерево кластеров.
   *
   * Перемещение кластера не меняет ни сами ключевые слова, ни их привязку,
   * поэтому тянуть весь проект заново незачем: раньше после каждого
   * перетаскивания заново грузились проект и ВСЕ ключи (в крупных проектах
   * это четверть миллиона строк), из-за чего перемещение ощущалось долгим.
   */
  async function refreshClusters(pId: string) {
    try {
      const response = await api.get(
        `/api/projects/${pId}/clusters?t=${Date.now()}`,
      );
      clusters.value = response.data.clusters || [];
      keywordsLogger.log("Clusters refreshed:", clusters.value.length);
    } catch (error) {
      errorsLogger.error("Error refreshing clusters:", error);
      throw error;
    }
  }

  // Перемещение кластера
  async function moveCluster(
    clusterId: number,
    newParentId: number | null,
    position?: "before" | "after" | "inside",
    targetClusterId?: number,
    skipHistory = false,
    skipRefresh = false,
  ) {
    if (!projectId.value) return;

    // Отключаем установку processingOperation здесь, так как она уже установлена в ClusterTreeItem
    // Это позволит правильно отображать операцию в индикаторе

    // Находим кластер и его старого родителя (рекурсивно)
    const findClusterWithParent = (
      clusters: Cluster[],
      id: number,
      parentId: number | null = null,
    ): { cluster: Cluster | null; parentId: number | null } => {
      for (const cluster of clusters) {
        if (cluster.id === id) {
          return { cluster, parentId };
        }
        if (cluster.children && cluster.children.length > 0) {
          const result = findClusterWithParent(
            cluster.children,
            id,
            cluster.id,
          );
          if (result.cluster) {
            return result;
          }
        }
      }
      return { cluster: null, parentId: null };
    };

    const { cluster, parentId: oldParentId } = findClusterWithParent(
      clusters.value,
      clusterId,
    );
    if (!cluster) return;

    try {
      const requestData: any = { parent_id: newParentId };
      if (position) {
        requestData.position = position;
        requestData.target_cluster_id = targetClusterId;
      }

      const response = await api.put(
        `/api/projects/${projectId.value}/clusters/${clusterId}/move`,
        requestData,
      );

      if (response.data.success) {
        keywordsLogger.log("Move cluster successful, refreshing clusters...");
        // Обновляем только дерево кластеров: ключевые слова перемещение
        // не затрагивает. При групповом перемещении вызывающий код передаёт
        // skipRefresh и обновляет дерево один раз после всей пачки.
        if (!skipRefresh) {
          await refreshClusters(projectId.value);
        }

        keywordsLogger.log(
          "After reload - clusters order:",
          clusters.value.map((c, index) => ({
            index,
            id: c.id,
            name: c.name,
            parent_id: c.parent_id,
          })),
        );

        // Добавляем действие в историю
        if (!skipHistory) {
          // Функция для поиска кластера по ID во всем дереве
          const findClusterById = (
            clusters: Cluster[],
            id: number,
          ): Cluster | null => {
            for (const cluster of clusters) {
              if (cluster.id === id) return cluster;
              if (cluster.children) {
                const found = findClusterById(cluster.children, id);
                if (found) return found;
              }
            }
            return null;
          };

          const parentCluster = newParentId
            ? findClusterById(clusters.value, newParentId)
            : null;
          const oldParentCluster = oldParentId
            ? findClusterById(clusters.value, oldParentId)
            : null;

          const action: ActionData = {
            type: "cluster_move",
            description: "", // Keep empty for backward compatibility
            descriptionKey: "undoRedo.actions.moveCluster",
            descriptionParams: {
              name: cluster.name,
              oldParent:
                oldParentCluster?.name ||
                (oldParentId ? "clusters.unknown" : "clusters.topLevel"),
              newParent:
                parentCluster?.name ||
                (newParentId ? "clusters.unknown" : "clusters.topLevel"),
            },
            timestamp: Date.now(),
            payload: {
              clusterId,
              clusterName: cluster.name,
              oldParentId,
              newParentId,
            },
          };
          addAction(action);

          toast.success(t("toasts.clusterMovedSuccess"));
        }
      }
    } catch (error) {
      if (!skipHistory) {
        toast.error(t("toasts.clusterMoveFailed"));
      }
      throw error;
    }
  }

  return {
    // State
    projectId,
    project,
    keywords,
    stats,
    clusters,
    selectedKeywordIds,
    isLoading,
    isProcessingClusters,
    processingOperation,
    currentTab,
    filters,
    showExcludedMode,

    // Computed
    filteredByTab,
    filteredKeywords,
    selectedKeywords,
    totalVolume,
    hasActiveFilters,
    activeFilters,
    advancedFilters,
    activeClusterFilter,
    clustersTree,
    unclusteredCount,

    // Actions
    loadKeywords,
    refreshStats,
    addKeywords,
    updateKeywords,
    removeKeywords,
    setCurrentTab,
    selectAllKeywords,
    deselectAllKeywords,
    toggleKeywordSelection,
    isKeywordSelected,
    updateFilters,
    applyFilters,
    clearFilters,
    collectKeywords,
    applyCleaningFilters,
    createClusters,
    deleteAllClusters,
    renameCluster,
    deleteCluster,
    createManualCluster,
    moveKeywordsToCluster,
    createSubcluster,
    setShowExcluded,
    restoreKeywords,
    deleteKeywordsPermanently,
    applyAdvancedFilters,
    clearAdvancedFilters,
    applyAdvancedGroupFilters,
    applyQuickFilter,
    clearQuickFilter,
    setActiveClusterFilter,
    updateClustersWithKeywords,
    loadClusters,
    refreshClusters,
    syncClustersWithKeywords,
    updateStats,
    moveCluster,
  };
});
